import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";

const GOLD = "#D4AF37";
const PIE_COLORS = ["#D4AF37", "#8B7A2E", "#5C5220", "#3A3416"];

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
      <h3 className="text-white font-medium mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);

  const signups = (d?.signups_series || []).map((s) => ({ date: s.date.slice(5), count: s.count }));
  const planData = Object.entries(d?.plan_distribution || {}).map(([name, value]) => ({ name, value }));
  const roleData = Object.entries(d?.role_distribution || {}).map(([name, value]) => ({ name, value }));
  const conv = d?.trial_conversion || { trials: 0, active: 0 };
  const convPct = conv.trials ? Math.round((conv.active / conv.trials) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5"><div className="text-[#71717A] text-xs uppercase tracking-wider">Total users</div><div className="font-heading text-3xl text-white mt-1">{d?.total_users ?? "—"}</div></div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5"><div className="text-[#71717A] text-xs uppercase tracking-wider">Verified</div><div className="font-heading text-3xl text-white mt-1">{d?.verified_users ?? "—"}</div></div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5"><div className="text-[#71717A] text-xs uppercase tracking-wider">Trials started</div><div className="font-heading text-3xl text-white mt-1">{conv.trials}</div></div>
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5"><div className="text-[#71717A] text-xs uppercase tracking-wider">Trial → paid</div><div className="font-heading text-3xl fv-gold-text mt-1">{convPct}%</div></div>
      </div>

      <Panel title="New signups (last 14 days)">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={signups}>
            <XAxis dataKey="date" stroke="#52525B" fontSize={11} tickLine={false} />
            <YAxis stroke="#52525B" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
            <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Active plan distribution">
          {planData.some((p) => p.value > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={planData.filter((p) => p.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {planData.map((p, i) => <Cell key={p.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", textTransform: "capitalize" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#71717A] py-16 text-center">No active subscriptions yet.</p>}
        </Panel>
        <Panel title="Top user activity">
          <div className="space-y-3">
            {(d?.top_activity || []).map(([kind, count]) => {
              const max = d.top_activity[0][1] || 1;
              return (
                <div key={kind}>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="text-[#D4D4D8] capitalize">{kind}</span><span className="text-[#71717A]">{count}</span></div>
                  <div className="h-2 rounded-full bg-white/8 overflow-hidden"><div className="h-full fv-gold-gradient" style={{ width: `${(count / max) * 100}%` }} /></div>
                </div>
              );
            })}
            {(d?.top_activity || []).length === 0 && <p className="text-sm text-[#71717A]">No activity yet.</p>}
            <div className="pt-3 mt-2 border-t border-white/8 text-xs text-[#71717A]">Roles: {roleData.map((r) => `${r.value} ${r.name}`).join(" · ")}</div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
